import {
  Box,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCompanyController } from 'api/controllers/CompanyController';
import { useCounterPartyController } from 'api/controllers/CounterPartyController';
import { useInvoiceController } from 'api/controllers/InvoiceController';

import {
  Company,
  CounterpartyCreateUpdate,
  CounterpartyType,
  Invoice,
  InvoicePatch,
  InvoiceSection,
  InvoiceStages,
  EntryType
} from 'openapi';

import { Approvals } from 'components/forms/InvoiceVerificationForms/Approvals';
import { CreatedDocumentForm } from 'components/forms/InvoiceVerificationForms/CreatedDocumentForm';
import { InvoiceDataForm } from 'components/forms/InvoiceVerificationForms/InvoiceDataForm';
import {
  AdditionalInvoiceFields,
  ReceiverForm
} from 'components/forms/InvoiceVerificationForms/ReceiverForm';
import { SupplierForm } from 'components/forms/InvoiceVerificationForms/SupplierForm';

import { useTranslations } from 'context/TranslationContext';

import { DocumentConfig } from 'utils/config/documentEntries';
import { COMPANY_PARAMETER, ID_PARAMETER } from 'utils/constants/constants';
import {
  CREATED_DOCUMENT_STEPPER_SECTIONS,
  INVOICE_STEPPER_SECTIONS
} from 'utils/constants/invoices';
import { ActiveStep } from 'utils/enums/Invoice';
import { getInvoiceStepperActiveStep } from 'utils/helpers/invoiceHelpers';
import { InvoiceSectionKey } from 'utils/interfaces/InvoiceProps';
import {
  mapInvoiceForReceiverSubmit,
  mapInvoiceForSupplierSubmit,
  mapInvoiceForInvoiceSubmit,
  mapInvoiceForSummarySubmit
} from 'utils/mappers/invoice';
import { AppRoutesEnum } from 'utils/routes';

import { loaderWithBackdropWrapper } from 'styles/components/LoaderStyle';

import { isRequestPendingAtom } from 'state/state';

import { CreateCounterpartyOverview } from '../InvoiceVerificationForms/CreateCounterpartyOverview';

interface RevenueVerificationStepperProps {
  currentInvoice: Invoice;
  triggeredReprompt: boolean;
  setCurrentInvoice: React.Dispatch<React.SetStateAction<Invoice | null>>;
  refreshInvoice: (newCompanyId: number) => Promise<void>;
  documentConfig: DocumentConfig;
}

export const RevenueVerificationStepper = ({
  currentInvoice,
  triggeredReprompt,
  setCurrentInvoice,
  refreshInvoice,
  documentConfig
}: RevenueVerificationStepperProps) => {
  const navigate = useNavigate();
  const { translate } = useTranslations();
  const { getAllCompanies } = useCompanyController();
  const {
    updateInvoice,
    unverifyInvoiceById,
    approveInvoicesByIds,
    rejectInvoicesByIds,
    saveInvoiceData,
    verifyCreatedDocument,
    saveInvoiceSectionData,
    returnToValidatedById,
    settleInvoices
  } = useInvoiceController();
  const { createNewCounterParty } = useCounterPartyController();

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  const isCreatedDocument = !currentInvoice.filePath;

  const [activeStep, setActiveStep] = useState(
    getInvoiceStepperActiveStep(currentInvoice)
  );
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [isOnMiddleStep, setIsOnMiddleStep] = useState(false);
  const [documentFormData, setDocumentFormData] = useState<Invoice>();
  const [cpIsNotFoundInDatabase, setCpIsNotFoundInDatabase] = useState(false);
  const [isReimbursement, setIsReimbursement] = useState(
    currentInvoice.isReimbursement
  );
  const [isRequestPending, setRequestPending] = useAtom(isRequestPendingAtom);

  const fetchCompanies = useCallback(async () => {
    const companies = await getAllCompanies();
    setAllCompanies(companies);
  }, [getAllCompanies]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    setActiveStep(getInvoiceStepperActiveStep(currentInvoice));
  }, [currentInvoice.stage]);

  const paramsChangeHandler = useCallback(
    (
      companyParameter: string,
      stageParameter: keyof typeof InvoiceStages,
      invoiceParameter: string
    ) => {
      const route =
        currentInvoice.entryType === EntryType.REVENUE
          ? AppRoutesEnum.REVENUE_VERIFICATION
          : AppRoutesEnum.INVOICE_VERIFICATION;

      navigate(
        route
          .replace(COMPANY_PARAMETER, companyParameter)
          .replace(ID_PARAMETER, invoiceParameter)
      );
    },
    [navigate, currentInvoice.documentType]
  );

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = async (section: InvoiceSection) => {
    setRequestPending(true);
    try {
      await unverifyInvoiceById(
        Number(currentInvoice.companyId),
        Number(currentInvoice.id),
        section
      );
      refreshInvoice(currentInvoice.companyId!);
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    } finally {
      setRequestPending(false);
    }
  };

  const updateInvoiceData = useCallback(
    async (invoiceSection: InvoiceSectionKey, requestBody: InvoicePatch) => {
      await updateInvoice(
        Number(currentInvoice.companyId),
        Number(currentInvoice.id),
        invoiceSection,
        requestBody
      );
    },
    [currentInvoice.id, updateInvoice, currentInvoice.companyId]
  );

  const handleVerify = useCallback(
    async (
      values: InvoicePatch & AdditionalInvoiceFields,
      section: InvoiceSectionKey
    ) => {
      let requestBody;
      let requestValues = values;

      if (
        section === InvoiceSection.COUNTERPARTY &&
        !values.counterpartyId &&
        values.counterpartyName
      ) {
        const createBody: CounterpartyCreateUpdate = {
          name: values.counterpartyName,
          vatNumber: values.counterpartyVatNumber,
          registrationNumber: values.counterpartyRegistrationNumber,
          ibans: [],
          type: CounterpartyType.SUPPLIER
        };

        try {
          const createdCounterparty = await createNewCounterParty(
            Number(currentInvoice.companyId),
            createBody
          );
          requestValues = {
            ...values,
            counterpartyId: createdCounterparty.id
          };
        } catch {
          return;
        }
      }

      switch (section) {
        case InvoiceSection.COMPANY:
          paramsChangeHandler(
            values.companyId!.toString(),
            currentInvoice.stage!,
            currentInvoice.id!.toString()
          );
          requestBody = mapInvoiceForReceiverSubmit(requestValues);
          break;
        case InvoiceSection.COUNTERPARTY:
          requestBody = mapInvoiceForSupplierSubmit(requestValues);
          break;
        case InvoiceSection.DOCUMENT_DATA:
          requestBody = mapInvoiceForInvoiceSubmit(requestValues);
          break;
        case InvoiceSection.SUMMARY:
          requestBody = mapInvoiceForSummarySubmit(requestValues);
          break;
        default:
          requestBody = {};
          break;
      }

      setRequestPending(true);
      try {
        await updateInvoiceData(section, requestBody);
        if (section !== InvoiceSection.SUMMARY) {
          handleNext();
        }
        if (section === InvoiceSection.COUNTERPARTY) {
          setIsOnMiddleStep(false);
        }
      } finally {
        setRequestPending(false);
        refreshInvoice(values.companyId! || Number(currentInvoice.companyId));
      }
    },
    [
      createNewCounterParty,
      currentInvoice.id,
      currentInvoice.stage,
      currentInvoice.companyId,
      refreshInvoice,
      paramsChangeHandler,
      updateInvoiceData
    ]
  );

  const saveCreatedDocumentData = useCallback(
    async (values: InvoicePatch) => {
      if (!currentInvoice.id || !currentInvoice.companyId) return;
      setRequestPending(true);
      try {
        const result = await saveInvoiceData(
          currentInvoice.companyId,
          currentInvoice.id,
          values
        );
        setCurrentInvoice(result);
      } finally {
        setRequestPending(false);
      }
    },
    [saveInvoiceData, currentInvoice.id, currentInvoice.companyId]
  );

  const saveInvoiceSection = useCallback(
    async (values: InvoicePatch, section: InvoiceSectionKey) => {
      if (!currentInvoice.id || !currentInvoice.companyId) return;
      setRequestPending(true);

      try {
        await saveInvoiceSectionData(
          currentInvoice.companyId,
          currentInvoice.id,
          section,
          values
        );
      } finally {
        setRequestPending(false);
      }
    },
    [saveInvoiceSectionData, currentInvoice.id, currentInvoice.companyId]
  );

  const handleVerifyCreatedDocument = useCallback(
    async (values: InvoicePatch) => {
      if (!currentInvoice.id || !currentInvoice.companyId) return;

      let { counterpartyId } = values;

      if (!counterpartyId && values.counterpartyName) {
        const createBody: CounterpartyCreateUpdate = {
          name: values.counterpartyName,
          vatNumber: values.counterpartyVatNumber,
          registrationNumber: values.counterpartyRegistrationNumber,
          ibans: [],
          type: CounterpartyType.SUPPLIER
        };

        try {
          const createdCounterparty = await createNewCounterParty(
            currentInvoice.companyId,
            createBody
          );
          counterpartyId = createdCounterparty.id;
        } catch {
          return;
        }
      }

      await verifyCreatedDocument(currentInvoice.companyId, currentInvoice.id, {
        ...values,
        counterpartyId
      });
    },
    [
      createNewCounterParty,
      verifyCreatedDocument,
      currentInvoice.id,
      currentInvoice.companyId
    ]
  );

  const handleApprove = async () => {
    setRequestPending(true);
    try {
      const result = await approveInvoicesByIds([Number(currentInvoice.id)]);
      setCurrentInvoice(result);
      if (currentInvoice.stage === InvoiceStages.VALIDATED) {
        handleNext();
        return;
      }
      setActiveStep((prev) => prev - 1);
    } finally {
      setRequestPending(false);
    }
  };

  const handleSettle = async () => {
    setRequestPending(true);
    try {
      const result = await settleInvoices({
        ids: [Number(currentInvoice.id)]
      });
      setCurrentInvoice(result);

      if (currentInvoice.stage === InvoiceStages.VALIDATED) {
        handleNext();
        return;
      }
      setActiveStep((prev) => prev - 1);
    } finally {
      setRequestPending(false);
    }
  };

  const handleReject = async (reason: string) => {
    setRequestPending(true);
    try {
      await rejectInvoicesByIds([Number(currentInvoice.id!)], reason);
      if (currentInvoice.stage === InvoiceStages.VALIDATED) {
        handleNext();
        return;
      }
      setActiveStep((prev) => prev - 1);
    } finally {
      setRequestPending(false);
    }
  };

  const handleReturnToValidated = async () => {
    setRequestPending(true);
    try {
      const result = await returnToValidatedById(
        Number(currentInvoice.companyId),
        Number(currentInvoice.id)
      );
      setActiveStep((prev) => prev - 1);
      setCurrentInvoice(result);
    } finally {
      setRequestPending(false);
    }
  };

  const handleApprovalsBack = useCallback(
    () => handleBack(InvoiceSection.DOCUMENT_DATA),
    [handleBack]
  );
  const handleCreatedDocumentBack = useCallback(
    () => handleBack(InvoiceSection.COMPANY),
    [handleBack]
  );

  const handleGoToMiddleStep = useCallback(() => {
    setIsOnMiddleStep(true);
  }, []);
  const handleBackFromMiddleStep = useCallback(() => {
    setIsOnMiddleStep(false);
  }, []);

  const stepLabelKey = (labelKey: string) => {
    if (labelKey === 'labels.counterparty' && isOnMiddleStep) {
      return 'labels.createCounterparty';
    }

    if (labelKey === 'labels.counterparty' && isReimbursement) {
      return 'labels.reimbursement';
    }

    return labelKey;
  };

  return (
    <Box sx={{ width: '100%', mb: 2.5 }}>
      <Stepper activeStep={activeStep}>
        {(isCreatedDocument
          ? CREATED_DOCUMENT_STEPPER_SECTIONS
          : INVOICE_STEPPER_SECTIONS
        ).map((labelKey) => {
          return (
            <Step key={labelKey}>
              <StepLabel>
                {isSm ? translate(stepLabelKey(labelKey)) : ''}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <Box sx={{ position: 'relative' }}>
        {isCreatedDocument && activeStep === 0 && (
          <Box>
            {isOnMiddleStep ? (
              <CreateCounterpartyOverview
                currentInvoice={currentInvoice}
                counterpartyFormData={documentFormData}
                handleBack={handleBackFromMiddleStep}
                handleCreate={handleVerifyCreatedDocument}
                cpIsNotFoundInDatabase={cpIsNotFoundInDatabase}
              />
            ) : (
              <CreatedDocumentForm
                documentConfig={documentConfig}
                currentInvoice={currentInvoice}
                handleVerify={handleVerifyCreatedDocument}
                handleSave={saveCreatedDocumentData}
                handleGoToMiddleStep={handleGoToMiddleStep}
                setDocumentFormData={setDocumentFormData}
                setCpIsNotFoundInDatabase={setCpIsNotFoundInDatabase}
              />
            )}
          </Box>
        )}
        {!isCreatedDocument && activeStep === ActiveStep.RECEIVER && (
          <ReceiverForm
            allCompanies={allCompanies}
            currentInvoice={currentInvoice}
            handleVerify={handleVerify}
            setIsReimbursement={setIsReimbursement}
            documentConfig={documentConfig}
          />
        )}
        {!isCreatedDocument && activeStep === ActiveStep.SUPPLIER && (
          <Box>
            {isOnMiddleStep ? (
              <CreateCounterpartyOverview
                currentInvoice={currentInvoice}
                counterpartyFormData={documentFormData}
                handleBack={handleBackFromMiddleStep}
                handleCreate={handleVerify}
                cpIsNotFoundInDatabase={cpIsNotFoundInDatabase}
              />
            ) : (
              <SupplierForm
                currentInvoice={currentInvoice}
                handleVerify={handleVerify}
                handleBack={handleBack}
                handleGoToMiddleStep={handleGoToMiddleStep}
                saveInvoiceSection={saveInvoiceSection}
                setCounterpartyFormData={setDocumentFormData}
                setCpIsNotFoundInDatabase={setCpIsNotFoundInDatabase}
              />
            )}
          </Box>
        )}
        {!isCreatedDocument && activeStep === ActiveStep.INVOICE_DATA && (
          <InvoiceDataForm
            currentInvoice={currentInvoice}
            handleVerify={handleVerify}
            handleSaveSection={saveInvoiceSection}
            handleBack={handleBack}
            triggerReprompt={triggeredReprompt}
            documentConfig={documentConfig}
          />
        )}
        {(activeStep === ActiveStep.APPROVALS ||
          activeStep === ActiveStep.APPROVED ||
          activeStep === ActiveStep.PAID ||
          activeStep === ActiveStep.REJECTED) && (
          <Approvals
            documentConfig={documentConfig}
            currentInvoice={currentInvoice}
            handleBack={
              isCreatedDocument
                ? handleCreatedDocumentBack
                : handleApprovalsBack
            }
            handleBackFromMiddleStep={handleBackFromMiddleStep}
            handleApprove={handleApprove}
            handleReject={handleReject}
            handleReturnToValidated={handleReturnToValidated}
            handleSettle={handleSettle}
            handleSaveData={handleVerify}
          />
        )}

        {isRequestPending && (
          <Box sx={loaderWithBackdropWrapper}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );
};
